import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, AlertTriangle, FileText, Clock, Sigma, CircleCheck, Search, ExternalLink, Filter,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuditOverview } from '@/api/hooks/useCtsAudit';
import {
  frameworkPercent, notYetAssessable,
  type Engagement, type FrameworkReadiness,
} from '@/api/types/cts-audit.types';

// ── TC5 — the Audit Center landing ───────────────────────────────────────────────────────────
//
// ⚠ THE TENANT'S VIEW OF ITS OWN AUDITS — not the auditor's, which lives at /auditor/* with its
// own session and scope gate, untouched by TC5.
//
// ⚠ AN ENGAGEMENT'S SCOPE IS A FROZEN CONTROL SET, resolved at grant. The framework chips on a row
// say how that scope was BUILT (provenance); they are never what decides visibility.

const PANEL = 'rounded-[10px] border border-cert-neutral-border bg-bg-surface';
const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.1em] text-text-secondary';
const GRID = 'minmax(0,1.5fr) 190px 130px 170px 220px 130px';

const fmt = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
      : '—';

const daysUntil = (iso: string): number =>
  Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);

/**
 * ⚠ THE ROW STATUS IS DERIVED, and it prefers the WORST TRUE THING.
 *
 * A live engagement with an overdue request reads as Overdue, because that is the fact someone has
 * to act on. Revoked and expired outrank it: there is nothing to chase on an engagement the
 * auditor can no longer reach. ⓘ Nothing stores this — a stored badge would drift from the dates
 * that produced it, and drift silently.
 */
type RowStatus = 'revoked' | 'expired' | 'overdue' | 'expiring' | 'pending' | 'active';

function rowStatus(e: Engagement): RowStatus {
  if (e.window_state === 'revoked') return 'revoked';
  if (e.window_state === 'expired') return 'expired';
  if (e.requests_overdue > 0) return 'overdue';
  if (e.window_state === 'pending_activation') return 'pending';
  if (daysUntil(e.expires_at) <= 14) return 'expiring';
  return 'active';
}

const STATUS_STYLE: Record<RowStatus, { label: string; dot: string; text: string; bg: string }> = {
  revoked:  { label: 'Revoked',  dot: 'bg-cert-expired-fg', text: 'text-cert-expired-fg', bg: 'bg-cert-expired-bg/40' },
  expired:  { label: 'Expired',  dot: 'bg-text-faint',      text: 'text-text-secondary',  bg: 'bg-white/[0.05]' },
  overdue:  { label: 'Overdue',  dot: 'bg-cert-expired-fg', text: 'text-cert-expired-fg', bg: 'bg-cert-expired-bg/40' },
  expiring: { label: 'Expiring', dot: 'bg-cert-pending-fg', text: 'text-cert-pending-fg', bg: 'bg-cert-pending-bg/40' },
  pending:  { label: 'Invited',  dot: 'bg-text-faint',      text: 'text-text-secondary',  bg: 'bg-white/[0.05]' },
  active:   { label: 'Active',   dot: 'bg-cert-valid-fg',   text: 'text-cert-valid-fg',   bg: 'bg-cert-valid-bg/40' },
};

const initials = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function StatCard({ icon: Icon, value, label, caption, tone, bar }: {
  icon: typeof Users; value: string | number; label: string; caption: string;
  tone: string; bar?: { pct: number; tone: string } | 'filter';
}) {
  const zero = value === 0;
  return (
    <div className={cn(PANEL, 'flex flex-col px-[15px] py-[13px]')}>
      <div className="flex items-start justify-between">
        {/* ⚠ A ZERO IS MUTED. An accent is an attention signal; a zero is the absence of one. */}
        <span className={cn('text-[24px] font-bold leading-none tabular-nums',
          zero ? 'text-text-faint' : tone)}>{value}</span>
        <span className={cn('flex h-[22px] w-[22px] items-center justify-center rounded-[6px] bg-white/[0.06]',
          zero ? 'text-text-faint' : tone)}>
          <Icon className="h-[13px] w-[13px]" aria-hidden="true" />
        </span>
      </div>
      <span className="mt-[9px] text-[12.5px] font-medium text-text-primary">{label}</span>
      <span className="mt-[1px] text-[11.5px] text-text-secondary">{caption}</span>
      <span className="mt-auto pt-[10px]">
        {bar === 'filter' ? (
          <span className="flex items-center gap-[5px] text-[11px] text-text-faint">
            <Filter className="h-[11px] w-[11px]" aria-hidden="true" /> Filter
          </span>
        ) : bar ? (
          <span className="flex h-[3px] w-full overflow-hidden rounded-full bg-bg-surface-2">
            <span className={cn('block h-full', bar.tone)} style={{ width: `${bar.pct}%` }} />
          </span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * ⚠ THE SEGMENTED BAR IS THE WHOLE FRAMEWORK, and the grey is load-bearing.
 *
 * Unmapped, N/A, never-started and unknown render as neutral grey — visibly present, never
 * coloured as failure. The rail says so in words, because a reader who assumes grey means "bad"
 * draws exactly the wrong conclusion from a sparsely-mapped framework.
 */
export function ReadinessBar({ r, className }: { r: FrameworkReadiness; className?: string }) {
  const segs: [number, string][] = [
    [r.met, 'bg-cert-valid-fg'],
    [r.partially, 'bg-cert-pending-fg'],
    [r.not_met, 'bg-cert-expired-fg'],
    [r.not_started, 'bg-text-faint/45'],
    [r.unknown, 'bg-text-faint/35'],
    [r.unmapped, 'bg-text-faint/28'],
    [r.not_applicable, 'bg-text-faint/20'],
  ];
  const total = Math.max(r.total, 1);
  return (
    <span className={cn('flex h-[6px] w-full overflow-hidden rounded-[3px] bg-bg-surface-2', className)}>
      {segs.map(([n, tone], i) => n > 0 && (
        <span key={i} className={cn('block h-full', tone)} style={{ width: `${(n / total) * 100}%` }} />
      ))}
    </span>
  );
}

export function AuditCenterPage() {
  const { data, isLoading, isError } = useAuditOverview();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | RowStatus>('all');

  const rows = useMemo(() => {
    const all = data?.engagements ?? [];
    const n = q.trim().toLowerCase();
    return all.filter((e) => {
      if (status !== 'all' && rowStatus(e) !== status) return false;
      if (!n) return true;
      const hay = `${e.auditor_display_name} ${e.audit_firm_name} ` +
        e.provenance.map((p) => p.name ?? '').join(' ');
      return hay.toLowerCase().includes(n);
    });
  }, [data, q, status]);

  const stats = useMemo(() => {
    const all = data?.engagements ?? [];
    const live = all.filter((e) => e.window_state === 'active');
    return {
      live: live.length,
      open: all.reduce((n, e) => n + e.requests_open, 0),
      overdue: all.reduce((n, e) => n + e.requests_overdue, 0),
      total: all.reduce((n, e) => n + e.requests_total, 0),
      done: all.reduce((n, e) => n + e.requests_fulfilled, 0),
      expiring: live.filter((e) => daysUntil(e.expires_at) <= 14).length,
      attention: live.filter((e) => e.requests_overdue > 0 || daysUntil(e.expires_at) <= 14).length,
    };
  }, [data]);

  // ⚠ NULL, not 0%, when nothing has been requested. "No requests yet" and "none fulfilled" are
  // different facts, and a confident 0% is the one that reads as failure.
  const completion = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : null;

  if (isLoading) {
    return <div className="space-y-[14px] p-[24px]"><Skeleton className="h-[110px] w-full" /><Skeleton className="h-[300px] w-full" /></div>;
  }
  if (isError || !data) {
    return <p className="p-[24px] text-[13px] text-cert-expired-fg">The audit centre could not be loaded.</p>;
  }

  return (
    <div className="p-[24px]">
      <div className="mb-[18px] flex flex-wrap items-start gap-[16px]">
        <div className="min-w-0 flex-1">
          <h1 className="text-[25px] font-bold text-text-primary">Audit Center</h1>
          <p className="mt-[5px] max-w-[70ch] text-[13px] leading-[1.55] text-text-secondary">
            Manage every audit engagement in one place — track status, access expirations,
            evidence-request progress, and items requiring attention. Open an engagement to view
            the full audit workflow.
          </p>
        </div>
        {/* ⚠ Auditors are INVITED and revoked in settings; this page watches the audits that
            produced. Sending the reader there beats duplicating the grant surface here. */}
        <Link to="/settings/auditor-access"
          className={cn(PANEL, 'flex shrink-0 items-center gap-[7px] px-[13px] py-[9px] text-[13px] text-text-primary transition-colors hover:border-accent-cyan')}>
          Manage auditor access
          <ExternalLink className="h-[13px] w-[13px] text-text-faint" aria-hidden="true" />
        </Link>
      </div>

      <div className="mb-[18px] grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-[12px]">
        <StatCard icon={Users} value={stats.live} label="Active auditors" caption="live engagements"
          tone="text-accent-cyan" bar={{ pct: 100, tone: 'bg-accent-cyan/70' }} />
        <StatCard icon={AlertTriangle} value={stats.attention} label="Requires attention"
          caption="overdue or expiring" tone="text-cert-expired-fg" bar="filter" />
        <StatCard icon={FileText} value={stats.open} label="Open requests"
          caption="awaiting evidence" tone="text-accent-cyan" bar="filter" />
        <StatCard icon={Clock} value={stats.overdue} label="Overdue items"
          caption="past due date" tone="text-cert-expired-fg" bar="filter" />
        <StatCard icon={Sigma} value={stats.expiring} label="Expiring soon"
          caption="within 14 days" tone="text-cert-pending-fg" bar="filter" />
        <StatCard icon={CircleCheck}
          value={completion === null ? '—' : `${completion}%`} label="Completion"
          caption={stats.total === 0 ? 'nothing requested yet' : `${stats.done} of ${stats.total} fulfilled`}
          tone="text-cert-valid-fg" bar={{ pct: completion ?? 0, tone: 'bg-cert-valid-fg/80' }} />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_290px] gap-[16px] max-[1100px]:grid-cols-1">
        <div className="min-w-0">
          <div className="mb-[12px] flex flex-wrap items-center gap-[10px]">
            <div className="relative min-w-[280px] flex-1">
              <Search className="pointer-events-none absolute left-[12px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text-faint" aria-hidden="true" />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search auditor, firm, engagement, or framework…" aria-label="Search engagements"
                className="w-full rounded-[9px] border border-cert-neutral-border bg-bg-surface py-[10px] pl-[36px] pr-[12px] text-[13.5px] text-text-primary placeholder:text-text-faint focus:border-accent-cyan focus:outline-none" />
            </div>
            <select value={status} aria-label="Filter by status"
              onChange={(e) => setStatus(e.target.value as 'all' | RowStatus)}
              className="rounded-[9px] border border-cert-neutral-border bg-bg-surface px-[12px] py-[10px] text-[13px] text-text-secondary focus:border-accent-cyan focus:outline-none">
              <option value="all">All statuses</option>
              {(Object.keys(STATUS_STYLE) as RowStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_STYLE[s].label}</option>
              ))}
            </select>
          </div>

          {/* ⚠ A NAMED REGION. Every status word also appears in the filter's option list, so a
              claim about what a ROW says is only checkable against a container. */}
          <div className={cn(PANEL, 'overflow-hidden')} role="region" aria-label="Engagements">
            <div className="grid gap-[14px] px-[16px] py-[10px]" style={{ gridTemplateColumns: GRID }}>
              {['Auditor / Engagement', 'Frameworks', 'Status', 'Expires', 'Evidence progress', 'Requests']
                .map((h) => <span key={h} className={EYEBROW}>{h}</span>)}
            </div>

            {rows.length === 0 ? (
              <p className="border-t border-border-subtle px-[16px] py-[22px] text-[13px] text-text-secondary">
                {data.engagements.length === 0
                  // ⚠ "No auditor yet" is a different state from "nothing matched", and only one
                  // of them has a next step.
                  ? <>No auditor has been given access yet.{' '}
                      <Link to="/settings/auditor-access" className="font-medium text-accent-cyan hover:underline">
                        Invite an auditor
                      </Link>.</>
                  : 'No engagement matches those filters.'}
              </p>
            ) : rows.map((e) => {
              const st = STATUS_STYLE[rowStatus(e)];
              const days = daysUntil(e.expires_at);
              return (
                <div key={e.token_id}
                  className="grid items-center gap-[14px] border-t border-border-subtle px-[16px] py-[13px]"
                  style={{ gridTemplateColumns: GRID }}>
                  <div className="flex min-w-0 items-center gap-[11px]">
                    <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] bg-bg-surface-2 text-[11px] font-semibold text-text-secondary">
                      {initials(e.audit_firm_name)}
                    </span>
                    <span className="min-w-0">
                      <Link to={`/compliance/workspace/audit/${e.token_id}`}
                        className="block truncate text-[13.5px] font-medium text-text-primary hover:text-accent-cyan">
                        {e.audit_firm_name}
                      </Link>
                      <span className="mt-[1px] block truncate text-[11.5px] text-text-faint">
                        {e.auditor_display_name}
                      </span>
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-[5px]">
                    {e.provenance.length === 0
                      ? <span className="text-[11.5px] italic text-text-faint">No scope</span>
                      : e.provenance.map((p, i) => (
                        <span key={p.source_framework_reference ?? i}
                          className="rounded-[4px] bg-bg-surface-2 px-[7px] py-[2px] font-mono text-[10.5px] text-text-secondary">
                          {p.name ?? 'Direct'}
                        </span>
                      ))}
                  </div>

                  <span className={cn('inline-flex w-fit items-center gap-[6px] rounded-full px-[9px] py-[3px] text-[11.5px] font-medium', st.bg, st.text)}>
                    <span className={cn('h-[6px] w-[6px] rounded-full', st.dot)} />
                    {st.label}
                  </span>

                  <span className="text-[12.5px]">
                    {/* ⚠ An elapsed window and a near one never look alike, and neither looks like
                        a healthy one. The date sits beneath, so the badge can be read against the
                        fact that produced it. */}
                    <span className={cn(
                      e.window_state === 'expired' ? 'text-cert-expired-fg'
                        : days <= 14 ? 'font-medium text-cert-pending-fg' : 'text-text-secondary')}>
                      {e.window_state === 'revoked' ? 'Revoked'
                        : e.window_state === 'expired' ? `Expired ${Math.abs(days)}d ago`
                        : `${days} days left`}
                    </span>
                    <span className="mt-[1px] block text-[11px] text-text-faint">
                      Access to {fmt(e.expires_at)}
                    </span>
                  </span>

                  <span className="flex items-center gap-[10px]">
                    <span className="flex h-[5px] min-w-0 flex-1 overflow-hidden rounded-full bg-bg-surface-2">
                      <span className="block h-full bg-accent-cyan/75"
                        style={{ width: `${e.requests_total ? (e.requests_fulfilled / e.requests_total) * 100 : 0}%` }} />
                    </span>
                    <span className="shrink-0 font-mono text-[11.5px] tabular-nums text-text-secondary">
                      {e.requests_fulfilled}/{e.requests_total}
                    </span>
                  </span>

                  <span className="text-[12px]">
                    <span className="block text-text-secondary">{e.requests_open} open</span>
                    {/* Overdue is a different fact from volume, not a shade of it. */}
                    {e.requests_overdue > 0 && (
                      <span className="mt-[1px] block font-medium text-cert-expired-fg">
                        {e.requests_overdue} overdue
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <aside className={cn(PANEL, 'h-fit px-[15px] py-[14px]')} aria-label="Framework readiness">
          <h2 className="flex items-center gap-[7px] text-[13px] font-semibold text-text-primary">
            <CircleCheck className="h-[14px] w-[14px] text-accent-cyan" aria-hidden="true" />
            Framework readiness
          </h2>
          {/* ⚠ THE LEGEND IS THE POINT. A reader who assumes grey means "failing" draws exactly the
              wrong conclusion from a sparsely-mapped framework — a platform content gap, not a
              tenant failure. */}
          <p className="mt-[5px] text-[11.5px] leading-[1.5] text-text-faint">
            {/* ⚠ BOTH DENOMINATORS ARE NAMED, here and on the engagement page. This rail is the
                tenant's WHOLE programme; an engagement's readiness is over the controls that
                auditor was granted. The two can legitimately differ, and a rail labelled only
                "Framework readiness" beside a list of engagements invites exactly the comparison
                that would make one of them look wrong. */}
            Across your whole programme. Grey = not yet assessable, never counted as failing.
          </p>

          {data.readiness.length === 0 ? (
            <p className="mt-[12px] text-[12.5px] text-text-secondary">
              No framework is in an audit scope yet.
            </p>
          ) : (
            <ul className="mt-[14px] space-y-[14px]">
              {data.readiness.map((r) => {
                const pct = frameworkPercent(r);
                const open = notYetAssessable(r);
                return (
                  <li key={r.framework_reference}>
                    <div className="flex items-baseline justify-between gap-[8px]">
                      <span className="truncate text-[12.5px] font-medium text-text-primary">{r.name}</span>
                      <span className="shrink-0 text-[12.5px] font-semibold tabular-nums text-text-primary">
                        {pct === null ? '—' : `${pct}%`}
                      </span>
                    </div>
                    <ReadinessBar r={r} className="mt-[6px]" />
                    <div className="mt-[5px] flex items-baseline justify-between gap-[8px]">
                      <span className="text-[11px] text-text-faint">
                        {r.met} of {r.total} requirements met
                      </span>
                      {open > 0 && (
                        <span className="shrink-0 text-[11px] text-accent-cyan">{open} open</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <Link to="/compliance/workspace/frameworks"
            className="mt-[14px] inline-flex items-center gap-[4px] text-[12px] font-medium text-accent-cyan hover:underline">
            View all frameworks ›
          </Link>
        </aside>
      </div>
    </div>
  );
}
